import React, { memo } from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { MessageSquare, Zap, GitBranch, CheckCircle2, XCircle, Clock, Loader2 } from "lucide-react";
import { clsx } from "clsx";

interface ExecutionData {
  status: 'completed' | 'failed' | 'running' | 'pending' | 'skipped';
  duration_ms?: number;
  [key: string]: any;
}

const StatusBadge = ({ status }: { status: ExecutionData['status'] }) => {
  if (!status) return null;

  return (
    <div className={clsx(
      "absolute -top-3 -right-3 flex items-center justify-center w-6 h-6 rounded-full shadow-sm border bg-white z-10",
      status === 'completed' && "text-green-600 border-green-200",
      status === 'failed' && "text-red-600 border-red-200",
      status === 'running' && "text-blue-600 border-blue-200",
      status === 'skipped' && "text-gray-400 border-gray-200",
    )}>
      {status === 'completed' && <CheckCircle2 size={14} />}
      {status === 'failed' && <XCircle size={14} />}
      {status === 'running' && <Loader2 size={14} className="animate-spin" />}
      {status === 'skipped' && <div className="w-2 h-2 rounded-full bg-gray-300" />}
    </div>
  );
};

const ExecutionStats = ({ duration }: { duration?: number }) => {
  if (duration === undefined) return null;
  return (
    <div className="absolute -bottom-5 w-full text-center">
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-[10px] text-gray-500 font-medium">
        <Clock size={10} />
        {duration}ms
      </div>
    </div>
  );
};

const NodeWrapper = ({
  children,
  className,
  selected,
  execution,
}: {
  children: React.ReactNode;
  className?: string;
  selected?: boolean;
  execution?: ExecutionData;
}) => {
  const statusColors = {
    completed: "border-green-500 ring-2 ring-green-100",
    failed: "border-red-500 ring-2 ring-red-100",
    running: "border-blue-500 ring-4 ring-blue-100 animate-pulse",
    pending: "opacity-50 grayscale",
    skipped: "border-gray-300 border-dashed opacity-60",
  };

  const baseStyle = execution?.status ? statusColors[execution.status] : (selected ? "border-blue-500" : "border-gray-200");

  return (
    <div
      className={clsx(
        "px-4 py-2 shadow-md rounded-md border-2 bg-white min-w-[150px] relative transition-all duration-300",
        baseStyle,
        className
      )}
    >
      <StatusBadge status={execution?.status as any} />
      {children}
      <ExecutionStats duration={execution?.duration_ms} />
    </div>
  );
};

export const TriggerNode = memo(({ data, selected }: NodeProps) => {
  const execution = data.execution as ExecutionData | undefined;

  return (
    <NodeWrapper selected={selected} execution={execution} className={!execution ? "border-blue-200 bg-blue-50" : "bg-blue-50/50"}>
      <div className="flex items-center gap-2">
        <Zap size={16} className="text-blue-600" />
        <div className="text-sm font-bold text-blue-900">
          {data.label as string}
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-3 h-3 bg-blue-500"
      />
    </NodeWrapper>
  );
});

export const WorkflowNode = memo(({ data, selected }: NodeProps) => {
  const execution = data.execution as ExecutionData | undefined;

  return (
    <NodeWrapper selected={selected} execution={execution} className={!execution ? "border-green-200 bg-green-50" : "bg-green-50/50"}>
      <div className="flex items-center gap-2">
        <Zap size={16} className="text-green-600" />
        <div className="text-sm font-bold text-green-900">
          {data.label as string}
        </div>
      </div>
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 bg-green-500"
      />
    </NodeWrapper>
  );
});

export const ActionNode = memo(({ data, selected }: NodeProps) => {
  const execution = data.execution as ExecutionData | undefined;

  return (
    <NodeWrapper selected={selected} execution={execution} className={!execution ? "border-purple-200 bg-purple-50" : "bg-purple-50/50"}>
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 bg-purple-500"
      />
      <div className="flex items-center gap-2">
        <MessageSquare size={16} className="text-purple-600" />
        <div className="text-sm font-bold text-purple-900">
          {data.label as string}
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        className="w-3 h-3 bg-purple-500"
      />
    </NodeWrapper>
  );
});

export const ConditionNode = memo(({ data, selected }: NodeProps) => {
  const execution = data.execution as ExecutionData | undefined;

  return (
    <NodeWrapper selected={selected} execution={execution} className={!execution ? "border-orange-200 bg-orange-50" : "bg-orange-50/50"}>
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 bg-orange-500"
      />
      <div className="flex items-center gap-2">
        <GitBranch size={16} className="text-orange-600" />
        <div className="text-sm font-bold text-orange-900">
          {data.label as string}
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        id="true"
        className="w-3 h-3 bg-green-500 -left-2"
        style={{ left: "25%" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        className="w-3 h-3 bg-red-500 -right-2"
        style={{ left: "75%" }}
      />
      <div className="flex justify-between text-[10px] mt-2 text-gray-500 font-medium">
        <span>True</span>
        <span>False</span>
      </div>
    </NodeWrapper>
  );
});

export const AINode = memo(({ data, selected }: NodeProps) => {
  const execution = data.execution as ExecutionData | undefined;

  return (
    <NodeWrapper selected={selected} execution={execution} className={!execution ? "border-orange-200 bg-orange-50" : "bg-orange-50/50"}>
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 bg-orange-500"
      />
      <div className="flex items-center gap-2">
        <GitBranch size={16} className="text-orange-600" />
        <div className="text-sm font-bold text-orange-900">
          {data.label as string}
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        id="true"
        className="w-3 h-3 bg-green-500 -left-2"
        style={{ left: "25%" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        className="w-3 h-3 bg-red-500 -right-2"
        style={{ left: "75%" }}
      />
      <div className="flex justify-between text-[10px] mt-2 text-gray-500 font-medium">
        <span>Confident</span>
        <span>Uncertain</span>
      </div>
    </NodeWrapper>
  );
});

export const HumanNode = memo(({ data, selected }: NodeProps) => {
  const execution = data.execution as ExecutionData | undefined;

  return (
    <NodeWrapper selected={selected} execution={execution} className={!execution ? "border-orange-200 bg-orange-50" : "bg-orange-50/50"}>
      <Handle
        type="target"
        position={Position.Top}
        className="w-3 h-3 bg-orange-500"
      />
      <div className="flex items-center gap-2">
        <GitBranch size={16} className="text-orange-600" />
        <div className="text-sm font-bold text-orange-900">
          {data.label as string}
        </div>
      </div>
      <Handle
        type="source"
        position={Position.Bottom}
        id="true"
        className="w-3 h-3 bg-green-500 -left-2"
        style={{ left: "25%" }}
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="false"
        className="w-3 h-3 bg-red-500 -right-2"
        style={{ left: "75%" }}
      />
      <div className="flex justify-between text-[10px] mt-2 text-gray-500 font-medium">
        <span>Approved</span>
        <span>Rejected</span>
      </div>
    </NodeWrapper>
  );
});
